import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router'

import { type RouterProps } from 'apps/types'

import { CensusProvider } from './CensusProvider'
import { CompletePage } from './CompletePage'
import { EditAllowancesPage } from './EditAllowancesPage'
import { BankingPage } from './finalize/BankingPage'
import { NextStepsPage } from './finalize/NextStepsPage'
import { AutomaticDeductionsPage } from './finalize/payroll/AutomaticDeductionsPage'
import { PayrollConfigProvider } from './finalize/PayrollConfigProvider'
import { PayrollPage } from './finalize/PayrollPage'
import { PaySchedulePage } from './finalize/PaySchedulePage'
import { PayScheduleProvider } from './finalize/PayScheduleProvider'
import { RemainingInfoPage } from './finalize/RemainingInfoPage'
import { type FeatureFlags } from './FlagProvider'
import { OnboardingLayout } from './Layout'
import { PageViewTracker } from './PageViewTracker'
import { QuotePage } from './quote'
import { QuoteReviewPage } from './quote/QuoteReviewPage'
import { QuoteSelfServeExplorerProvider } from './quote/QuoteSelfServeExplorerProvider'
import { QuoteProvider } from './QuoteProvider'
import { ScrollRestoration } from './ScrollRestoration'
import { AdditionalInfoPage } from './setup/AdditionalInfoPage'
import { SetupAllowancesPage } from './setup/AllowancesPage'
import { AllowanceStrategyPage } from './setup/AllowanceStrategyPage'
import { CurrentBenefitsPage } from './setup/benefits/CurrentBenefitsPage'
import { NewBenefitsPage } from './setup/benefits/NewBenefitsPage'
import { BenefitsPage } from './setup/BenefitsPage'
import { CensusConfirmationPage } from './setup/census/CensusConfirmationPage'
import { CensusPage } from './setup/CensusPage'
import { EmbeddedCensusDependentsPage } from './setup/EmbeddedCensusDependentsPage'
import { SetupIntroPage } from './setup/IntroPage'
import { PendingRatesPage } from './setup/PendingRatesPage'
import { ProspectProvider } from './setup/ProspectProvider'
import { BasicInfoPage } from './welcome/BasicInfoPage'
import { EmbeddedRegistrationPage } from './welcome/EmbeddedRegistrationPage'
import { EmployeeLandingPage } from './welcome/EmployeeLandingPage'
import { GetStartedPage } from './welcome/GetStartedPage'
import { HowItWorksPage } from './welcome/HowItWorksPage'
import { WelcomeIntroPage } from './welcome/IntroPage'
import { RegisterPage } from './welcome/RegisterPage'
import { ResumePage } from './welcome/ResumePage'
import { NotFoundPage } from '../../apps/pages/NotFoundPage'

interface CommonProps {
  authenticated?: boolean;
  assets?: {
    images?: Record<string, string>;
  };
  employerId: string | null;
  mapboxAccessToken?: string;
  featureFlags?: FeatureFlags
  embeddedOnboardingBearer?: string | null;
  editingAllowances?: boolean;
}

type EmployerOnboardingPropsUnion = CommonProps & RouterProps

export function EmployerOnboarding ({
  authenticated,
  assets = {},
  employerId,
  mapboxAccessToken,
  router: Router = BrowserRouter,
  featureFlags,
  routerProps,
  embeddedOnboardingBearer,
  editingAllowances = false
}: EmployerOnboardingPropsUnion) {
  useEffect(() => {
    window.posthog?.set_config({ capture_pageview: 'history_change' })
  }, [])

  return (
    <Router {...routerProps}>
      <PageViewTracker />
      <ScrollRestoration />
      <Routes>
        <Route
          path='/employers/onboarding'
          element={<OnboardingLayout employerId={employerId} featureFlags={featureFlags} embeddedOnboardingBearer={embeddedOnboardingBearer} editingAllowances={editingAllowances} />}
        >
          <Route index element={<NavigatePreserveSearch to='welcome' replace />} />
          <Route path='resume' element={<ResumePage />} />
          <Route path='welcome'>
            <Route index element={<NavigatePreserveSearch to='get-started' replace />} />
            <Route path='get-started' element={<GetStartedPage />} />
            <Route path='employee' element={<EmployeeLandingPage assets={assets} />} />
            <Route path='intro' element={<WelcomeIntroPage />} />
            <Route path='how-it-works' element={<HowItWorksPage assets={assets} />} />
            <Route path='register' element={<RegisterPage authenticated={authenticated} />} />
            <Route path='embedded-set-password' element={<EmbeddedRegistrationPage />} />
            <Route path='info' element={<BasicInfoPage />} />
          </Route>
          <Route element={<QuoteProvider />}>
            <Route path='setup' element={<ProspectProvider />}>
              <Route index element={<Navigate to='intro' replace />} />
              <Route path='intro' element={<SetupIntroPage />} />
              <Route path='allowances' element={<SetupAllowancesPage assets={assets} />} />
              <Route element={<CensusProvider />}>
                <Route path='census'>
                  <Route index element={<CensusPage />} />
                  <Route path='embedded' element={<EmbeddedCensusDependentsPage />} />
                  <Route path='confirm' element={<CensusConfirmationPage />} />
                </Route>
                <Route path='additional-info' element={<AdditionalInfoPage />} />
              </Route>
              <Route path='benefits'>
                <Route index element={<BenefitsPage />} />
                <Route path='new' element={<NewBenefitsPage />} />
                <Route path='current' element={<CurrentBenefitsPage />} />
              </Route>
              <Route path='pending-rates' element={<PendingRatesPage />} />
              <Route path='strategy' element={<AllowanceStrategyPage assets={assets} />} />
            </Route>
            <Route path='quote' element={<ProspectProvider />}>
              <Route element={<QuoteSelfServeExplorerProvider />}>
                <Route path='edit' element={<EditAllowancesPage />} />
                <Route index element={<QuotePage />} />
                <Route path='review' element={<QuoteReviewPage />} />
                <Route path='one-more-reminder' element={<PendingRatesPage isReminderPage />} />
              </Route>
            </Route>
            <Route path='finalize'>
              <Route index element={<Navigate to='next-steps' replace />} />
              <Route path='next-steps' element={<NextStepsPage />} />
              <Route element={<PayrollConfigProvider />}>
                <Route path='payroll'>
                  <Route index element={<PayrollPage />} />
                  <Route path='automatic-deductions' element={<AutomaticDeductionsPage />} />
                </Route>
                <Route element={<PayScheduleProvider />}>
                  <Route path='pay-schedule' element={<PaySchedulePage />} />
                </Route>
              </Route>
              <Route
                path='info'
                element={
                  <RemainingInfoPage mapboxAccessToken={mapboxAccessToken} />
                }
              />
              <Route path='banking' element={<BankingPage />} />
            </Route>
            <Route path='complete' element={<CompletePage />} />
          </Route>
          <Route path='*' element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Router>
  )
}

function NavigatePreserveSearch ({ to, replace }: { to: string; replace?: boolean }) {
  const location = useLocation()
  return <Navigate to={{ pathname: to, search: location.search }} replace={replace} />
}
